import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const StoreContext = createContext(null);

const read = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};

export function StoreProvider({ children }) {
  const [cart, setCart] = useState(() => read("shs_enquiry", []));
  const [wishlist, setWishlist] = useState(() => read("shs_wishlist", []));
  const [catalog, setCatalog] = useState([]);
  const [quickView, setQuickView] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.products().then(setCatalog).catch(() => {});
    api.site("settings").then((value) => setSettings((current) => ({ ...current, ...value }))).catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem("shs_enquiry", JSON.stringify(cart)); } catch { /* Keep the in-memory shortlist usable if storage is unavailable. */ }
  }, [cart]);
  useEffect(() => {
    try { localStorage.setItem("shs_wishlist", JSON.stringify(wishlist)); } catch { /* Keep saved designs usable for this page session. */ }
  }, [wishlist]);

  const productBySlug = useCallback((slug) => catalog.find((p) => p.slug === slug), [catalog]);

  const addToCart = useCallback((product, qty = null, silent = false) => {
    const quantity = qty === "" || qty == null ? null : Number(qty);
    setCart((prev) => {
      const found = prev.find((i) => i.slug === product.slug);
      if (found) return prev.map((i) => (i.slug === product.slug && quantity !== null ? { ...i, qty: quantity } : i));
      return [...prev, { slug: product.slug, qty: quantity }];
    });
    if (!silent) toast.success(`${product.name} added to enquiry`);
    if (!silent) setCartOpen(true);
  }, []);

  const setQty = useCallback((slug, qty) => {
    setCart((prev) =>
      prev.map((i) => (i.slug === slug ? { ...i, qty: qty === "" || qty == null ? null : Number(qty) } : i))
    );
  }, []);

  const removeFromCart = useCallback((slug) => setCart((prev) => prev.filter((i) => i.slug !== slug)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      if (prev.includes(product.slug)) {
        toast(`${product.name} removed from wishlist`);
        return prev.filter((s) => s !== product.slug);
      }
      toast.success(`${product.name} saved to wishlist`);
      return [...prev, product.slug];
    });
  }, []);

  const cartDetailed = useMemo(
    () =>
      cart
        .map((i) => ({ ...i, product: productBySlug(i.slug) }))
        .filter((i) => i.product),
    [cart, productBySlug]
  );

  const value = {
    cart, cartDetailed, addToCart, setQty, removeFromCart, clearCart,
    wishlist, toggleWishlist, catalog, productBySlug,
    cartCount: cart.length,
    quickView, setQuickView, cartOpen, setCartOpen,
    settings,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => useContext(StoreContext);
